import { api } from './base.api.client'
import type {
  AuthorizeResponse,
  Connection,
  ConnectionDetail,
  ConnectionSettings,
  HealthReport,
  TestConnectionResult,
  WhoAmI,
} from '@/core/types/api.types'

export interface ConnectionListing {
  connections: Connection[]
  /** Distinct workspaces across the matched connections. */
  workspaces: string[]
  /** Distinct tags across the matched connections. */
  tags: string[]
}

export const connectionsApi = {
  list: (connectorId?: string) =>
    api
      .get<ConnectionListing>('/connections', { connector_id: connectorId })
      .then((d) => d.connections),

  /** The list plus the filter bar's own options, in one round trip. */
  listing: (params: { connectorId?: string; workspaceId?: string; tag?: string } = {}) =>
    api.get<ConnectionListing>('/connections', {
      connector_id: params.connectorId,
      workspace_id: params.workspaceId,
      tag: params.tag,
    }),

  detail: (connectionId: string) =>
    api.get<ConnectionDetail>(`/connections/${encodeURIComponent(connectionId)}`),

  health: (connectionId: string) =>
    api.get<HealthReport>(`/connections/${encodeURIComponent(connectionId)}/health`),

  rename: (connectionId: string, label: string) =>
    connectionsApi.update(connectionId, { label }),

  /**
   * Change a connection's settings. Only the keys sent are written, so saving
   * one section never clears another.
   */
  update: (
    connectionId: string,
    patch: ConnectionSettings & { label?: string },
  ) =>
    api
      .patch<{ connection: Connection }>(
        `/connections/${encodeURIComponent(connectionId)}`,
        patch,
      )
      .then((d) => d.connection),

  revoke: (connectionId: string) =>
    api.delete<{ connection_id: string; revoked: boolean }>(
      `/connections/${encodeURIComponent(connectionId)}`,
    ),

  // --- connecting -----------------------------------------------------------

  /** api_key / bearer / basic / custom: one POST, straight to a connection. */
  connectWithFields: (
    connectorId: string,
    fields: Record<string, unknown>,
    label?: string,
    method?: string | null,
    settings?: ConnectionSettings,
  ) =>
    api
      .post<{ connection: Connection }>(`/auth/${encodeURIComponent(connectorId)}/connect`, {
        fields,
        label,
        method: method ?? undefined,
        ...(settings ?? {}),
      })
      .then((d) => d.connection),

  /** Verify credentials without storing them, so a typo is caught in the dialog. */
  testConnection: (
    connectorId: string,
    method: string | null,
    fields: Record<string, unknown>,
  ) =>
    api.post<TestConnectionResult>(`/auth/${encodeURIComponent(connectorId)}/test`, {
      fields,
      method: method ?? undefined,
    }),

  /**
   * oauth2 step 1: get the provider URL to send the user to.
   *
   * `fields` carries whatever the method declared — for a provider whose OAuth
   * app is issued per tenant, that is the app itself, and the authorize URL
   * cannot be built without it.
   */
  startAuthorize: (
    connectorId: string,
    payload: {
      label?: string
      scopes?: string[]
      redirect_url?: string
      method?: string | null
      fields?: Record<string, unknown>
    } & ConnectionSettings = {},
  ) =>
    api.post<AuthorizeResponse>(
      `/auth/${encodeURIComponent(connectorId)}/authorize`,
      payload,
    ),

  /** oauth2 step 2: redeem the code the provider handed back. */
  finishAuthorize: (connectorId: string, code: string, state: string) =>
    api
      .post<{ connection: Connection }>(`/auth/${encodeURIComponent(connectorId)}/callback`, {
        code,
        state,
      })
      .then((d) => d.connection),

  whoami: () => api.get<WhoAmI>('/auth/me'),
}
